import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {formatNumber} from 'accounting'

import {INCOGNITO_MASK} from '../../../Components/Private'
import {useIncognito} from '../../../hooks/useIncognito'
import {formatCurrency} from '../../../Utils/utils'

import './Sankey.less'

// Hand-rolled SVG Sankey. Visually identical to the Recover Analytics one, but the
// layout that recharts used to compute (node boxes + ribbon geometry) is done here
// so prod does not need a second charting library alongside chart.js.
//
// Data contract (same as Recover): nodes are { name, color, segment } and links are
// { source, target, value } where source/target are indices into nodes.
//
// Why imperative hover highlighting: setting React state on mouse move would
// re-render every ribbon. Instead each node/link carries a data-* attribute and we
// toggle opacity directly on the DOM; enter/leave are coalesced into one
// requestAnimationFrame so sliding between adjacent ribbons does not shimmer.

const NODE_WIDTH = 12
// right is recomputed per-render (see measureLastColumnWidth) to fit whatever the last
// column's labels actually need; this is only the fallback before that ever runs.
const MARGIN = {top: 24, right: 184, bottom: 32, left: 0}
// Floor for the measured right margin, so a chart with very short outcome names (or a
// font that hasn't loaded yet) never collapses the label column to near-zero.
const MIN_RIGHT_MARGIN = 60
// Gap between a bar's right edge and the start of the widest label that clears it.
const TEXT_GAP = 16
// The name line wraps onto a second line past this width ("Already a chargeback" etc.)
// instead of running wide and forcing a much bigger right margin.
const NAME_MAX_LINE_WIDTH = 110
const NAME_LINE_HEIGHT = 18
const MAX_NAME_LINES = 2

// A node's label is three lines ("<alerts> | <volume>" / "<name>" / "<pct> of total"), and
// it is centred on the node's bar. A node carrying two alerts is barely a pixel tall, so
// nothing but the gap between bars keeps two labels apart: NODE_PADDING has to clear a
// whole label, and NODE_SLOT (which sets the chart height) has to clear the padding too.
// Derive both from LABEL_HEIGHT so a taller label can never silently start overlapping -
// including the extra line a wrapped name can take, reserved for every node whether or
// not its own name actually wraps (a per-node variable gap would need a second layout pass).
const LABEL_HEIGHT = 56 + (MAX_NAME_LINES - 1) * NAME_LINE_HEIGHT
const NODE_PADDING = LABEL_HEIGHT + 8
const NODE_SLOT = NODE_PADDING + 36
const BASE_HEIGHT = 420

// Label text colours, theme-keyed (SVG text is painted in JS, so it cannot read
// Less tokens; both values are on-palette).
const labelColors = (dark) => ({
  primary: dark ? '#FCFCFC' : '#27272A', // Neutral50 / Neutral800
  muted: dark ? '#A1A1AA' : '#52525B', // Neutral400 / Neutral600 (AA on white card)
})

// The separator between the count and the volume. The root and network labels are drawn on
// top of their ribbons, so a fixed hairline colour would read on the card and disappear
// over the blue. Fading the label's own muted colour keeps it a hair either way.
const DIVIDER_OPACITY = 0.45

// The last column's labels have nothing to their right but MARGIN.right, sized to fit
// the widest one ("Already a chargeback"). Anchoring them to the bar's own right edge
// (like every other column) leaves whatever slack that label doesn't need dangling
// empty on the right; anchoring them to the chart's right edge instead keeps that
// same reserved width but flush-right, so short labels stop looking off-balance.
const LABEL_EDGE_PADDING = 8

// Real font metrics via a single shared canvas 2D context, so the label column reserves
// only what its own content needs instead of a fixed width sized for the longest string
// ever seen ("Already a chargeback") - a filtered mobile view with short outcome names
// gets its bars pulled much closer to the text.
let measureCtx = null
const textWidth = (text, font) => {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d')
  measureCtx.font = font
  return measureCtx.measureText(text).width
}

// Greedy word-wrap against real font metrics, capped at maxLines - once the last allowed
// line is reached, remaining words just keep appending to it instead of wrapping further,
// so a name longer than maxLines allows overflows that line rather than dropping words.
const wrapLabel = (text, maxWidth, font, maxLines) => {
  const words = text.split(' ')
  const lines = []
  let current = ''
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word
    if (!current || lines.length === maxLines - 1 || textWidth(candidate, font) <= maxWidth) {
      current = candidate
    } else {
      lines.push(current)
      current = word
    }
  })
  if (current) lines.push(current)
  return lines
}

// Widest of the label's lines, at the exact font/weight each <tspan> renders with. Mobile
// renders a different set of lines than desktop (see the Sankey component), so its width
// has to be measured against that actual content or the reserved margin drifts out of
// sync with what's drawn.
const labelWidth = (node, value, pct, incognito, t, isMobile, unit) => {
  const count = incognito ? INCOGNITO_MASK : formatNumber(value, 0, ',', '.')
  const volume = incognito ? INCOGNITO_MASK : formatCurrency(node.volume ?? 0, true)
  const nameLines = wrapLabel(node.name, NAME_MAX_LINE_WIDTH, '500 14px Inter', MAX_NAME_LINES)
  const nameWidth = Math.max(...nameLines.map((line) => textWidth(line, '500 14px Inter')))
  if (isMobile) {
    const countLine = textWidth(t('_count_ _unit_', {count, unit}), '400 14px Inter')
    const volumeLine = textWidth(t('_volume_, _percent_%', {volume, percent: pct}), '400 14px Inter')
    return Math.max(nameWidth, countLine, volumeLine)
  }
  const countVolumeLine =
    textWidth(count, '700 16px Inter') +
    8 +
    textWidth('|', '400 14px Inter') +
    8 +
    textWidth(volume, '700 16px Inter')
  const percentLine = textWidth(t('_percent_% of total', {percent: pct}), '400 14px Inter')
  return Math.max(countVolumeLine, nameWidth, percentLine)
}

// Longest-path depth per node index, from the link structure.
const nodeDepths = (nodes, links) => {
  const parents = new Map()
  links.forEach((l) => {
    if (!parents.has(l.target)) parents.set(l.target, [])
    parents.get(l.target).push(l.source)
  })
  const depth = new Array(nodes.length).fill(-1)
  const resolve = (i, seen) => {
    if (depth[i] >= 0) return depth[i]
    const ps = parents.get(i)
    if (!ps || ps.length === 0) {
      depth[i] = 0
      return 0
    }
    if (seen.has(i)) return 0 // cycle guard
    seen.add(i)
    const d = 1 + Math.max(...ps.map((p) => resolve(p, seen)))
    depth[i] = d
    return d
  }
  nodes.forEach((_, i) => resolve(i, new Set()))
  return depth
}

// Root nodes never appear as a link target; the "% of total" denominator is the sum
// of flows leaving them.
const rootIndices = (data) => {
  const targets = new Set(data.links.map((l) => l.target))
  return new Set(data.nodes.map((_, i) => i).filter((i) => !targets.has(i)))
}

const isNodeActive = (index, hover, links) => {
  if (!hover) return true
  if (hover.kind === 'node') return index === hover.index
  const link = links[hover.index]
  return link?.source === index || link?.target === index
}

const isLinkActive = (index, hover, links) => {
  if (!hover) return true
  if (hover.kind === 'link') return index === hover.index
  const link = links[index]
  return link?.source === hover.index || link?.target === hover.index
}

// Node boxes + ribbon geometry. Node value is max(inflow, outflow) so a column that
// only receives still sizes correctly. The tightest column sets the shared
// value->pixel scale, so ribbon widths stay comparable across the whole chart.
const buildLayout = (data, width, height, total, incognito, t, isMobile, unit) => {
  const {nodes, links} = data
  if (!width || !nodes.length) return null

  const depths = nodeDepths(nodes, links)
  const maxDepth = Math.max(0, ...depths)

  const outSum = new Array(nodes.length).fill(0)
  const inSum = new Array(nodes.length).fill(0)
  links.forEach((l) => {
    outSum[l.source] += l.value
    inSum[l.target] += l.value
  })
  const values = nodes.map((_, i) => Math.max(outSum[i], inSum[i]))

  const lastColumnWidth = Math.max(
    0,
    ...nodes.map((node, i) => {
      if (depths[i] !== maxDepth) return 0
      const pct = total > 0 ? Math.round((values[i] / total) * 100) : 0
      return labelWidth(node, values[i], pct, incognito, t, isMobile, unit)
    })
  )
  const margin = {...MARGIN, right: Math.max(MIN_RIGHT_MARGIN, Math.ceil(lastColumnWidth) + TEXT_GAP + LABEL_EDGE_PADDING)}

  const innerW = width - margin.left - margin.right
  const innerH = height - margin.top - margin.bottom

  const columns = []
  depths.forEach((d, i) => {
    if (!columns[d]) columns[d] = []
    columns[d].push(i)
  })
  // Largest value first within each column, so a tiny node (e.g. a network with a single
  // alert) never lands wedged directly above one with hundreds - the incoming node order
  // is whatever the backend/toChartData produced, not sorted by size.
  columns.forEach((col) => col.sort((a, b) => values[b] - values[a]))

  let scale = Infinity
  columns.forEach((col) => {
    const colTotal = col.reduce((sum, i) => sum + values[i], 0)
    const avail = innerH - (col.length - 1) * NODE_PADDING
    if (colTotal > 0 && avail > 0) scale = Math.min(scale, avail / colTotal)
  })
  if (!Number.isFinite(scale) || scale <= 0) scale = 1

  const xStep = maxDepth > 0 ? (innerW - NODE_WIDTH) / maxDepth : 0

  // Desktop pins every column to the top. Mobile's filtered single-service view is always
  // exactly 2 columns (one source bar next to its outcomes) and centres the short source
  // column instead, so it doesn't sit flush at the top with empty space below it.
  //
  // With 3+ columns (mobile's "All alerts" option) this same per-column centring breaks:
  // the outer columns (root, outcomes) each have far less padding overhead than the middle
  // column(s), so they end up needing barely any offset while a middle column full of
  // small-vs-large networks needs a large one - columns drift out of line with each other
  // and ribbons swing down then back up instead of flowing straight across. There's no
  // per-column fix for that without a real barycentric layout pass, so 3+ columns just stay
  // top-aligned like desktop.
  const centerColumns = isMobile && maxDepth === 1
  const boxes = new Array(nodes.length)
  columns.forEach((col, d) => {
    const colHeight =
      col.reduce((sum, i) => sum + Math.max(values[i] * scale, 1), 0) + (col.length - 1) * NODE_PADDING
    let y = centerColumns ? margin.top + Math.max(0, (innerH - colHeight) / 2) : margin.top
    col.forEach((i) => {
      const h = Math.max(values[i] * scale, 1)
      boxes[i] = {x: margin.left + d * xStep, y, w: NODE_WIDTH, h, value: values[i], depth: d}
      y += h + NODE_PADDING
    })
  })

  // Stack ribbons inside each node in link order (the flow is authored in a sensible
  // order, so we do not re-sort and cross ribbons unnecessarily).
  const outOffset = new Array(nodes.length).fill(0)
  const inOffset = new Array(nodes.length).fill(0)
  const ribbons = links.map((l, index) => {
    const s = boxes[l.source]
    const targetBox = boxes[l.target]
    const w = Math.max(l.value * scale, 1)
    const sy = s.y + outOffset[l.source] + w / 2
    const ty = targetBox.y + inOffset[l.target] + w / 2
    outOffset[l.source] += w
    inOffset[l.target] += w
    const sx = s.x + s.w
    const tx = targetBox.x
    const mid = (sx + tx) / 2
    return {index, w, sx, sy, tx, ty, d: `M${sx},${sy} C${mid},${sy} ${mid},${ty} ${tx},${ty}`}
  })

  return {boxes, ribbons, maxDepth}
}

const Sankey = ({data, theme, unit, isMobile, onNodeClick, onLinkClick}) => {
  const {t} = useTranslation()
  const incognito = useIncognito()
  const containerRef = useRef(null)
  const rafRef = useRef(null)
  const pendingRef = useRef(null)
  const [width, setWidth] = useState(0)
  const [tip, setTip] = useState(null)

  const dark = theme === 'dark'
  const colors = labelColors(dark)
  const resolvedUnit = unit || t('alerts')
  const displayCount = (value) => (incognito ? INCOGNITO_MASK : formatNumber(value, 0, ',', '.'))
  const displayVolume = (value) => (incognito ? INCOGNITO_MASK : formatCurrency(value ?? 0, true))

  // Lighter at rest, theme-keyed: overlapping translucent ribbons otherwise compound
  // into a solid block and the bars stop reading as anchors. Hover lifts the active
  // ribbon back to full opacity.
  const stopOpacity = dark ? 0.42 : 0.5

  const total = useMemo(() => {
    const roots = rootIndices(data)
    return data.links.filter((l) => roots.has(l.source)).reduce((sum, l) => sum + l.value, 0)
  }, [data])

  const maxColNodes = useMemo(() => {
    const depths = nodeDepths(data.nodes, data.links)
    const perColumn = new Map()
    depths.forEach((d) => perColumn.set(d, (perColumn.get(d) ?? 0) + 1))
    return Math.max(1, ...perColumn.values())
  }, [data])

  // Height scales with the busiest column so the labels never overlap.
  const height = Math.max(BASE_HEIGHT, MARGIN.top + MARGIN.bottom + maxColNodes * NODE_SLOT)

  // Callback ref, not an effect: the chart returns null until the flow arrives, so an
  // effect with [] deps would attach to a container that was not mounted yet and the
  // width would stay 0 forever. Measure on mount, then track resizes.
  const observerRef = useRef(null)
  const setContainer = useCallback((node) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    containerRef.current = node
    if (!node) return
    setWidth(node.getBoundingClientRect().width)
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0
      setWidth((prev) => (Math.abs(prev - w) > 1 ? w : prev))
    })
    ro.observe(node)
    observerRef.current = ro
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  const layout = useMemo(
    () => buildLayout(data, width, height, total, incognito, t, isMobile, resolvedUnit),
    [data, width, height, total, incognito, t, isMobile, resolvedUnit]
  )

  const paintHighlight = useCallback((target) => {
    const root = containerRef.current
    if (!root) return
    const links = data.links
    root.querySelectorAll('[data-sankey-node]').forEach((el) => {
      const idx = Number(el.dataset.sankeyNode)
      el.style.opacity = isNodeActive(idx, target, links) ? '1' : '0.3'
    })
    root.querySelectorAll('[data-sankey-link]').forEach((el) => {
      const idx = Number(el.dataset.sankeyLink)
      el.style.opacity = isLinkActive(idx, target, links) ? '1' : '0.12'
    })
  }, [data.links])

  // Coalesce enter/leave pairs into a single rAF so moving between adjacent ribbons
  // does not briefly reset everything to full opacity (a visible shimmer).
  const scheduleHighlight = useCallback((target) => {
    pendingRef.current = target
    if (rafRef.current != null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      paintHighlight(pendingRef.current)
    })
  }, [paintHighlight])

  useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current) }, [])

  const showTip = (e, content) => {
    const box = containerRef.current?.getBoundingClientRect()
    if (!box) return
    setTip({x: e.clientX - box.left, y: e.clientY - box.top, content})
  }

  if (total === 0) return null

  return (
    <div ref={setContainer} className="dashboard-sankey" style={{height}}>
      {layout && (
        <svg width="100%" height={height} role="img" aria-label={t('Alert flow')}>
          <defs>
            {layout.ribbons.map((r) => {
              const link = data.links[r.index]
              return (
                <linearGradient
                  key={r.index}
                  id={`dashboard-sankey-link-${r.index}`}
                  x1={r.sx}
                  x2={r.tx}
                  y1={0}
                  y2={0}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor={data.nodes[link.source].color} stopOpacity={stopOpacity} />
                  <stop offset="100%" stopColor={data.nodes[link.target].color} stopOpacity={stopOpacity} />
                </linearGradient>
              )
            })}
          </defs>

          {layout.ribbons.map((r) => {
            const link = data.links[r.index]
            const source = data.nodes[link.source]
            const target = data.nodes[link.target]
            return (
              <g
                key={r.index}
                data-sankey-link={r.index}
                style={{cursor: onLinkClick ? 'pointer' : 'default', opacity: 1, transition: 'opacity 150ms ease'}}
                onMouseEnter={(e) => {
                  scheduleHighlight({kind: 'link', index: r.index})
                  showTip(e, {title: `${source.name} → ${target.name}`, value: link.value, volume: link.volume})
                }}
                onMouseMove={(e) => showTip(e, {title: `${source.name} → ${target.name}`, value: link.value, volume: link.volume})}
                onMouseLeave={() => { scheduleHighlight(null); setTip(null) }}
                onClick={() => onLinkClick?.(source, target, link.value)}
              >
                {/* Invisible wider hit area so thin ribbons stay easy to hover/click. */}
                <path d={r.d} stroke="transparent" strokeWidth={Math.max(r.w, 24)} fill="none" style={{pointerEvents: 'stroke'}} />
                <path d={r.d} stroke={`url(#dashboard-sankey-link-${r.index})`} strokeWidth={Math.max(r.w, 1)} fill="none" />
              </g>
            )
          })}

          {data.nodes.map((node, i) => {
            const box = layout.boxes[i]
            if (!box) return null
            const pct = total > 0 ? Math.round((box.value / total) * 100) : 0
            const isLastColumn = box.depth === layout.maxDepth
            // Desktop's outcome column has room to spare on the right, so it hugs that
            // edge (see the dynamic margin above). Mobile is narrow enough that hugging
            // the right edge instead pushes the text away from the bar it belongs to, so
            // it stays anchored to the bar like every other column, and every node's
            // (source and outcome alike) label leads with the name instead of the count.
            const labelX = isLastColumn && !isMobile ? width - LABEL_EDGE_PADDING : box.x + box.w + 10
            const labelAnchor = isLastColumn && !isMobile ? 'end' : 'start'
            // Centred on the bar by default, but a near-zero-value node (clamped to a 1px
            // bar) sitting at the very top or bottom of a column would otherwise centre its
            // label half off the edge of the canvas - keep it fully on screen instead.
            const rawLabelY = box.y + (box.h - LABEL_HEIGHT) / 2
            const labelY = Math.min(Math.max(rawLabelY, 4), height - LABEL_HEIGHT - 4)
            const nameLines = wrapLabel(node.name, NAME_MAX_LINE_WIDTH, '500 14px Inter', MAX_NAME_LINES)
            const activate = () => onNodeClick?.(node, i)
            return (
              <g
                key={node.segment ?? i}
                data-sankey-node={i}
                role={onNodeClick ? 'button' : undefined}
                tabIndex={onNodeClick ? 0 : undefined}
                aria-label={onNodeClick ? `${node.name}: ${displayCount(box.value)}` : undefined}
                style={{cursor: onNodeClick ? 'pointer' : 'default', opacity: 1, transition: 'opacity 150ms ease'}}
                onMouseEnter={(e) => {
                  scheduleHighlight({kind: 'node', index: i})
                  showTip(e, {title: node.name, value: box.value, volume: node.volume})
                }}
                onMouseMove={(e) => showTip(e, {title: node.name, value: box.value, volume: node.volume})}
                onMouseLeave={() => { scheduleHighlight(null); setTip(null) }}
                onClick={activate}
                onKeyDown={(e) => { if (onNodeClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); activate() } }}
              >
                <rect x={box.x} y={box.y} width={box.w} height={box.h} rx={4} ry={4} fill={node.color} />
                <text x={labelX} y={labelY} textAnchor={labelAnchor} dominantBaseline="hanging">
                  {isMobile ? (
                    <>
                      <tspan fill={node.color} fontSize="14" fontWeight="500">{nameLines[0]}</tspan>
                      {nameLines.slice(1).map((line, li) => (
                        <tspan
                          key={`name-${li}`}
                          x={labelX}
                          dy={NAME_LINE_HEIGHT}
                          fill={node.color}
                          fontSize="14"
                          fontWeight="500"
                        >
                          {line}
                        </tspan>
                      ))}
                      <tspan x={labelX} dy="20" fill={colors.muted} fontSize="14" fontWeight="400">
                        {t('_count_ _unit_', {count: displayCount(box.value), unit: resolvedUnit})}
                      </tspan>
                      <tspan x={labelX} dy="20" fill={colors.muted} fontSize="14" fontWeight="400">
                        {t('_volume_, _percent_%', {volume: displayVolume(node.volume), percent: pct})}
                      </tspan>
                    </>
                  ) : (
                    <>
                      <tspan fill={colors.primary} fontSize="16" fontWeight="700">{displayCount(box.value)}</tspan>
                      <tspan dx="8" fill={colors.muted} fillOpacity={DIVIDER_OPACITY} fontSize="14" fontWeight="400">
                        |
                      </tspan>
                      <tspan dx="8" fill={colors.primary} fontSize="16" fontWeight="700">
                        {displayVolume(node.volume)}
                      </tspan>
                      <tspan x={labelX} dy="22" fill={colors.primary} fontSize="14" fontWeight="500">
                        {nameLines[0]}
                      </tspan>
                      {nameLines.slice(1).map((line, li) => (
                        <tspan
                          key={`name-${li}`}
                          x={labelX}
                          dy={NAME_LINE_HEIGHT}
                          fill={colors.primary}
                          fontSize="14"
                          fontWeight="500"
                        >
                          {line}
                        </tspan>
                      ))}
                      <tspan x={labelX} dy="18" fill={colors.muted} fontSize="14" fontWeight="400">
                        {t('_percent_% of total', {percent: pct})}
                      </tspan>
                    </>
                  )}
                </text>
              </g>
            )
          })}
        </svg>
      )}

      {/* Dark tooltip box, matching the other charts (kept dark in both themes). */}
      {tip && (
        <div className="dashboard-sankey_tooltip" style={{left: tip.x, top: tip.y}}>
          <div className="dashboard-sankey_tooltip-title">{tip.content.title}</div>
          <div className="dashboard-sankey_tooltip-value">
            {displayCount(tip.content.value)} {resolvedUnit}
          </div>
          {typeof tip.content.volume === 'number' && (
            <div className="dashboard-sankey_tooltip-value">{displayVolume(tip.content.volume)}</div>
          )}
        </div>
      )}
    </div>
  )
}

export default Sankey
